import {
  MyIngredientsPageFilterSidebar,
  type MyIngredientsPageFilterSidebarProps,
} from './MyIngredientsPageFilterSidebar';

import type {
  Formula,
  Supplement,
} from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';

import { useMemo } from 'react';
import { Button } from '@design-system/Button';
import { NavBar } from '@design-system/NavBar';
import { Skeleton } from '@design-system/Skeleton';
import { useSearchParams } from 'react-router-dom';
import { sanitizeString } from '@/utils/string/sanitizeString';
import { SearchBarTrigger } from '@/components/shared/SearchBar';
import { ScrollArea, ScrollBar } from '@design-system/ScrollArea';
import { BookmarkButton } from '@/components/shared/BookmarkButton';
import { MyIngredientsPageBanner } from './MyIngredientsPageBanner';
import { MyIngredientsPageFormula } from './MyIngredientsPageFormula';
import { ToggleGroup, ToggleGroupItem } from '@design-system/ToggleGroup';
import { FILTER, type GroupedSupplement } from './MyIngredientsPage.model';
import { useGetSupplementListQuery } from '@viome/viome-base/src/usecase/Nutrition/NutritionQuery';
import { useGetProductsQuery } from '@viome/viome-base/src/usecase/Nutrition/NutritionQuery';

const groupSupplements = (supplements?: Supplement[]) => {
  if (!supplements?.length) return;

  const groupedSupplements = supplements.reduce(
    (acc, supplement) => {
      const { product_id, product_name, sub_product, formula } = supplement;

      {/* TODO: Remove the below logic if the ingredient data are not present */}
      if (!formula.blends.length && !formula.ingredients.length) return acc;

      const sanitizedSubProductName = sub_product ?? 'DEFAULT';

      const sanitizedProductName = sanitizeString(product_name, {
        lowerCase: true,
      });

      if (sanitizedProductName in acc) {
        acc[sanitizedProductName].formulas[sanitizedSubProductName] = formula;
      } else {
        acc[sanitizedProductName] = {
          productId: product_id,
          productName: product_name,
          formulas: {
            [sanitizedSubProductName]: formula,
          },
        };
      }

      return acc;
    },
    {} as Record<string, GroupedSupplement>,
  );

  return groupedSupplements;
};

const parseTypeFilterValues = (rawValue?: string | null) => {
  if (!rawValue) return;

  const parsedValue = rawValue.split('#').reduce(
    (acc, encodedValue) => {
      const [rawFilterId, rawFilterValues] = encodedValue.split(':');

      if (!rawFilterValues) return acc;

      const filterId = Number(rawFilterId);
      const values = rawFilterValues.split(';');

      if (Number.isNaN(filterId)) return acc;

      acc[filterId] = values;

      return acc;
    },
    {} as Record<number, string[]>,
  );

  return parsedValue;
};

const parseFilters = (rank: number, bookmarked?: boolean) => {
  const filters = [];

  if (!Number.isNaN(rank)) filters.push(rank);
  if (bookmarked) filters.push(0);

  return filters;
};

const getSelectedSupplementGroup = (
  groupedSupplements?: Record<string, GroupedSupplement>,
  productName?: string,
) => {
  if (!groupedSupplements) return;

  let selectedSupplement: GroupedSupplement | undefined;

  if (productName) {
    const sanitizedSelectedProductName = sanitizeString(productName, {
      lowerCase: true,
    });

    selectedSupplement = groupedSupplements[sanitizedSelectedProductName];
  }

  selectedSupplement ??= Object.values(groupedSupplements).at(0);

  return selectedSupplement;
};

const getFormulaAmount = (formula?: Formula) => {
  let amount = 0;

  if (!formula) return amount;

  const { ingredients, blends } = formula;

  if (!ingredients.length) return amount;

  amount += ingredients.length;

  if (!blends) return amount;

  blends.forEach((blend) => {
    amount += blend.ingredients?.length ?? 0;
  });

  return amount;
};

const renderProductOptions = (
  groupedSupplements?: Record<string, GroupedSupplement>,
  isLoading?: boolean,
) => {
  if (isLoading) {
    return Array.from({ length: 5 }).map((_, index) => (
      <Skeleton key={index} className="w-28 h-8 mx-1 my-2" />
    ));
  }

  if (!groupedSupplements) return null;

  const renderedProductOptions = Object.values(groupedSupplements).map(
    (option) => (
      <ToggleGroupItem key={option.productId} value={option.productName}>
        {option.productName}
      </ToggleGroupItem>
    ),
  );

  return renderedProductOptions;
};

export const MyIngredientsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterParams = Object.fromEntries(searchParams.entries());
  const { data: products } = useGetProductsQuery();

  const rankFilter = filterParams[FILTER.RANK];
  const typeFilter = filterParams[FILTER.TYPE];
  const productFilter = filterParams[FILTER.PRODUCT];
  const bookmarkedFilter = filterParams[FILTER.BOOKMARKED];
  const isFiltered = [rankFilter, typeFilter, bookmarkedFilter].some(Boolean);

  const isBookmarked = bookmarkedFilter === 'true';
  const parsedTypeFilters = parseTypeFilterValues(typeFilter);

  const { data: getSupplementListResponse, isLoading } =
    useGetSupplementListQuery(
      parsedTypeFilters,
      parseFilters(Number(rankFilter), isBookmarked),
    );

  const groupedSupplements = useMemo(
    () => groupSupplements(getSupplementListResponse),
    [getSupplementListResponse],
  );

  const selectedSupplementGroup = getSelectedSupplementGroup(
    groupedSupplements,
    productFilter,
  );
  const isProductSubscribed = useMemo(() => {
    if (!products || !selectedSupplementGroup) return false;
    
    const currentProduct = products.find(
      product => product.name.toLowerCase() === selectedSupplementGroup.productName.toLowerCase()
    );
    
    return currentProduct?.is_subscribed ?? false;
  }, [products, selectedSupplementGroup]);

  const sanitizedSelectedProductName = sanitizeString(
    selectedSupplementGroup?.productName,
    { lowerCase: true },
  );

  const formulaAmount = Object.values(
    selectedSupplementGroup?.formulas ?? {},
  ).reduce((acc, formula) => acc + getFormulaAmount(formula), 0);

  const handleOnToggleBookmarkedFilter = () => {
    setSearchParams((params) => {
      const value = isBookmarked ? 'false' : 'true';

      params.set(FILTER.BOOKMARKED, value);

      return params;
    });
  };

  const handleOnChangeProductName = (value: string) => {
    setSearchParams((params) => {
      if (!value) {
        params.delete(FILTER.PRODUCT);
      } else {
        params.set(FILTER.PRODUCT, value);
      }

      return params;
    });
  };

  const handleOnResetFilters = () => {
    setSearchParams((params) => {
      params.delete(FILTER.TYPE);
      params.delete(FILTER.RANK);
      params.delete(FILTER.BOOKMARKED);

      return params;
    });
  };

  const handleOnSubmitTypeFilters: MyIngredientsPageFilterSidebarProps['onSubmit'] =
    (filterValue) => {
      if (!filterValue) return;

      const encodedFilterValues = Object.entries(filterValue).map(
        ([name, value]) => `${name}:${value?.join(';')}`,
      );

      const joinedFilterValues = encodedFilterValues.join(',');

      setSearchParams((params) => {
        if (!joinedFilterValues) {
          params.delete(FILTER.TYPE);
        } else {
          params.set(FILTER.TYPE, joinedFilterValues);
        }

        return params;
      });
    };

  const productFirstName = sanitizedSelectedProductName?.split(' ')[0];
  const isDisplayFilter = sanitizedSelectedProductName.includes('supplements') || sanitizedSelectedProductName.includes('gut');

  return (
    <section
      data-product={productFirstName}
      className="transition-colors h-full bg-gradient-to-b data-[product='supplements']:from-[#F6FAF5] data-[product='supplements']:to-[#F9FDF9] data-[product='gut']:from-[#FBF9F5] data-[product='gut']:to-[#FDFDFD] data-[product='oral']:from-[#E9F5FF] data-[product='oral']:to-[#FFF8F5] from-[#E8F8FC] to-[#FAF5EB]"
    >
      <div className="grid gap-6 page-container">
        <NavBar>
          <SearchBarTrigger
            className="md:max-w-80 w-full"
            placeholder="Search Ingredients"
          />
        </NavBar>

        <ScrollArea>
          <ToggleGroup
            type="single"
            variant="text"
            onValueChange={handleOnChangeProductName}
            value={selectedSupplementGroup?.productName}
            className="flex-nowrap typography-h1 whitespace-nowrap"
          >
            {renderProductOptions(groupedSupplements, isLoading)}
          </ToggleGroup>

          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        {!isProductSubscribed && (
          <MyIngredientsPageBanner productName={productFirstName} />
        )}

        <ScrollArea>
          <div className="flex flex-row items-center gap-2 pb-3">
            {isDisplayFilter && (
              <MyIngredientsPageFilterSidebar
                initialValue={parsedTypeFilters}
                onSubmit={handleOnSubmitTypeFilters}
                productId={selectedSupplementGroup?.productId}
              />
            )}

            <BookmarkButton
              size="default"
              variant="toggle"
              data-focus={isBookmarked}
              bookmarked
              onClick={handleOnToggleBookmarkedFilter}
              className="typography-body h-9 pr-4 pl-3 text-primary [&_svg]:size-5"
            >
              Saved
            </BookmarkButton>
          </div>

          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        <div className="w-full typography-body text-secondary flex flex-row gap-1 items-center">
          {isFiltered && 'Filtered - '}
          Showing
          <span>
            {isLoading ? <Skeleton className="h-5 w-16" /> : formulaAmount}
          </span>
          ingredients
          {isFiltered && (
            <Button
              variant="flat"
              onClick={handleOnResetFilters}
              className="ml-auto typography-title text-primary p-0 h-auto"
            >
              Reset
            </Button>
          )}
        </div>

        {Object.entries(selectedSupplementGroup?.formulas ?? {}).map(
          ([name, formula]) => {
            return (
              <MyIngredientsPageFormula
                name={name}
                formula={formula}
                isLoading={isLoading}
                isSubProduct={name !== 'DEFAULT'}
                productId={selectedSupplementGroup?.productId}
              />
            );
          },
        )}
      </div>
    </section>
  );
};
